import './writer.css'

import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { Translates } from '@/services/repository/i18n'
import { BackButton } from '@/components/layout/NavBar'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'

const WJO_MARKERS = {
  wjo2: 'pass:WJO2',
  wjo3: 'pass:WJO3',
} as const

export function isNFCSupported(): boolean {
  return 'NDEFReader' in window
}

export interface PGSNFCWriterProps {
  onRead: (cardID: string) => void
  onReadError: () => void
  onInit: (success: boolean) => void
}

export const readNFC = (props: PGSNFCWriterProps) => {
  if (!('NDEFReader' in window)) {
    props.onInit(false)
    return () => { }
  }

  try {
    const ndef = new NDEFReader()
    const abortController = new AbortController()
    let aborted = false

    const onReadingHandler = (event: any) => {
      if (aborted) return

      const { serialNumber } = event as NDEFMessageEvent

      if (!serialNumber) {
        props.onReadError()
        return
      }

      const ucid = serialNumber.replaceAll(':', '').toUpperCase()
      props.onRead(ucid)
    }

    ndef.scan({ signal: abortController.signal }).then(
      () => {
        ndef.addEventListener('reading', onReadingHandler)
        ndef.addEventListener('readingerror', props.onReadError)
        props.onInit(true)
      },
      () => {
        props.onInit(false)
      }
    )

    return () => {
      aborted = true
      abortController.abort()
      ndef.removeEventListener('reading', onReadingHandler)
      ndef.removeEventListener('readingerror', props.onReadError)
    }
  } catch {
    props.onInit(false)
    return () => { }
  }
}

export interface PGSNFCWriterPopupProps {
  onRead: (cardID: string) => void
  onClose: () => void
  translates: Translates
}

export enum PGSNFCWriterPopupStatuses {
  error,
  unsupported,
  ready,
  readed,
}

export const PGSNFCWriterPopup = (props: PGSNFCWriterPopupProps) => {
  const { translates } = props
  const baseKey = 'elements.nfcwriter'
  const t = (k: string) => translates.for(`${baseKey}.${k}`)

  const [cardId, setCardId] = useState<string>()
  const [status, setStatus] = useState<PGSNFCWriterPopupStatuses>()

  useEffect(() => {
    return readNFC({
      onInit: (success: boolean) => {
        setStatus(
          success
            ? PGSNFCWriterPopupStatuses.ready
            : PGSNFCWriterPopupStatuses.unsupported
        )
      },
      onReadError: () => {
        setStatus(PGSNFCWriterPopupStatuses.error)
      },
      onRead: (newCardId) => {
        setStatus(PGSNFCWriterPopupStatuses.readed)
        setCardId(newCardId)
        props.onRead(newCardId)
        props.onClose()
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === PGSNFCWriterPopupStatuses.error) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.error')}</div>
      </div>
    )
  }

  if (status === PGSNFCWriterPopupStatuses.readed) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">
          {t('status.readed').replace('{{card_id}}', cardId ?? '')}
        </div>
      </div>
    )
  }

  if (status === PGSNFCWriterPopupStatuses.ready) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.insert_nfc_card')}</div>
      </div>
    )
  }

  if (status === PGSNFCWriterPopupStatuses.unsupported) {
    return (
      <div className="nfcwriter-popup">
        <div className="icon-box">
          <i className="svgfont-nfc" />
        </div>
        <div className="status">{t('status.unsupported')}</div>
      </div>
    )
  }

  return (
    <div className="nfcwriter-popup">
      <Spinner />
    </div>
  )
}

export const PGSNFCWriterPage = () => {
  const location = useLocation()
  const { services, session } = useAppContext()

  const [tagSerial, setTagSerial] = useState<string | null>(null)
  const [isReading, setIsReading] = useState(false)

  const readAbortRef = useRef<AbortController | null>(null)
  const readerRef = useRef<NDEFReader | null>(null)
  const onReadingRef = useRef<((e: any) => void) | null>(null)
  const onReadingErrorRef = useRef<(() => void) | null>(null)

  const [statusKey, setStatusKey] = useState<string | null>(null)

  const originLoginUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/login` : '/login'

  const parseBool = (v: string | null | undefined): boolean => {
    if (!v) return false
    const s = String(v).toLowerCase()
    return s === '1' || s === 'true' || s === 'yes' || s === 'on'
  }

  const getDefaultsFromQuery = () => {
    const sp = new URLSearchParams(location.search)

    const markersParam = sp.get('markers') ?? ''
    const markers = markersParam
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)

    const defaultWjo2 = parseBool(sp.get('wjo2')) || markers.includes('wjo2')
    const defaultWjo3 = parseBool(sp.get('wjo3')) || markers.includes('wjo3')

    // url can be "1/true" OR a full url
    const urlParam = sp.get('url')
    const addUrl =
      parseBool(sp.get('addUrl')) ||
      parseBool(urlParam) ||
      (!!urlParam && urlParam.startsWith('http'))

    const urlToWrite =
      urlParam && urlParam.startsWith('http') ? urlParam : originLoginUrl

    return {
      targets: { wjo2: defaultWjo2, wjo3: defaultWjo3 },
      addUrl,
      urlToWrite,
    }
  }

  const defaultsRef = useRef(getDefaultsFromQuery())

  const [targets, setTargets] = useState<{ wjo2: boolean; wjo3: boolean }>(
    defaultsRef.current.targets
  )
  const [addUrl, setAddUrl] = useState<boolean>(defaultsRef.current.addUrl)
  const [urlToWrite, setUrlToWrite] = useState<string>(
    defaultsRef.current.urlToWrite
  )

  // keep current UI values accessible in async handlers
  const targetsRef = useRef(targets)
  const addUrlRef = useRef(addUrl)
  const urlToWriteRef = useRef(urlToWrite)
  useEffect(() => {
    targetsRef.current = targets
  }, [targets])
  useEffect(() => {
    addUrlRef.current = addUrl
  }, [addUrl])
  useEffect(() => {
    urlToWriteRef.current = urlToWrite
  }, [urlToWrite])

  const isWritingRef = useRef(false)

  function decodeTextRecord(record: NDEFRecord): string | null {
    try {
      if (record.recordType !== 'text') return null

      const dataAny = (record as any).data
      // Some implementations return already-decoded string
      if (typeof dataAny === 'string') return dataAny

      if (!(dataAny instanceof DataView)) return null

      const bytes = new Uint8Array(
        dataAny.buffer,
        dataAny.byteOffset,
        dataAny.byteLength
      )
      if (bytes.length === 0) return ''

      // 1) Try RTD_TEXT payload: [status][lang...][text...]
      const status = bytes[0]
      const langLength = status & 0x3f
      const isUtf16 = (status & 0x80) !== 0

      if (1 + langLength <= bytes.length) {
        const textBytes = bytes.slice(1 + langLength)
        const parsed = new TextDecoder(isUtf16 ? 'utf-16' : 'utf-8').decode(
          textBytes
        )
        // If we got something sensible, use it
        if (parsed && parsed.trim().length > 0) return parsed
      }

      // 2) Fallback: treat whole payload as plain text bytes
      const enc = ((record as any).encoding as string | undefined) ?? 'utf-8'
      return new TextDecoder(enc, { fatal: false }).decode(bytes)
    } catch {
      return null
    }
  }

  function decodeAnyRecordToString(record: NDEFRecord): string | null {
    const asText = decodeTextRecord(record)
    if (asText != null) return asText

    const dataAny = (record as any).data
    if (dataAny instanceof DataView) {
      try {
        const bytes = new Uint8Array(
          dataAny.buffer,
          dataAny.byteOffset,
          dataAny.byteLength
        )
        const s = new TextDecoder('utf-8', { fatal: false }).decode(bytes)
        return s.replace(/\u0000/g, '').trim()
      } catch {
        return null
      }
    }

    if (typeof dataAny === 'string') return dataAny.trim()

    return null
  }

  function normalizeMarker(s: string): string {
    // remove control chars that may appear if payload wasn't parsed as RTD_TEXT
    return s.replace(/[\u0000-\u001f\u007f]/g, '').trim().toLowerCase()
  }

  const URI_PREFIXES = [
    '',
    'http://www.',
    'https://www.',
    'http://',
    'https://',
    'tel:',
    'mailto:',
    'ftp://anonymous:anonymous@',
    'ftp://ftp.',
    'ftps://',
    'sftp://',
    'smb://',
    'nfs://',
    'ftp://',
    'dav://',
    'news:',
    'telnet://',
    'imap:',
    'rtsp://',
    'urn:',
    'pop:',
    'sip:',
    'sips:',
    'tftp:',
    'btspp://',
    'btl2cap://',
    'btgoep://',
    'tcpobex://',
    'irdaobex://',
    'file://',
    'urn:epc:id:',
    'urn:epc:tag:',
    'urn:epc:pat:',
    'urn:epc:raw:',
    'urn:epc:',
    'urn:nfc:',
  ] as const

  function fixPossiblyBrokenUrl(url: string): string {
    const s = url.replace(/\u0000/g, '').trim()
    const lower = s.toLowerCase()

    // Heurystyki na już “zepsute” tagi (brakujące litery z początku)
    if (lower.startsWith('ps://')) return `htt${s}`          // ps://... -> https://...
    if (lower.startsWith('tps://')) return `h${s}`           // tps://... -> https://...
    if (lower.startsWith('ttps://')) return `h${s}`          // ttps://... -> https://...
    if (lower.startsWith('ttp://')) return `h${s}`           // ttp://... -> http://...
    if (lower.startsWith('tp://')) return `ht${s}`           // tp://... -> http://...

    return s
  }

  function encodeUriPayload(url: string): Uint8Array {
    const u = url.trim()

    // wybieramy NAJDŁUŻSZY pasujący prefix (zgodnie z RTD_URI)
    let bestCode = 0
    let bestPrefix = ''

    for (let code = 1; code < URI_PREFIXES.length; code++) {
      const p = URI_PREFIXES[code] ?? ''
      if (!p) continue
      if (u.startsWith(p) && p.length > bestPrefix.length) {
        bestPrefix = p
        bestCode = code
      }
    }

    const rest = u.slice(bestPrefix.length)
    const restBytes = new TextEncoder().encode(rest)

    const out = new Uint8Array(1 + restBytes.length)
    out[0] = bestCode
    out.set(restBytes, 1)
    return out
  }

  function decodeUriRecord(record: NDEFRecord): string | null {
    try {
      if (record.recordType !== 'url') return null
      const dataAny = (record as any).data
      if (!dataAny) return null

      // Some implementations may give url directly
      if (typeof dataAny === 'string') return fixPossiblyBrokenUrl(dataAny)

      const data = dataAny as DataView
      const bytes = new Uint8Array(
        data.buffer,
        data.byteOffset,
        data.byteLength
      )
      if (bytes.length === 0) return ''

      const first = bytes[0]

      // Jeśli pierwszy bajt wygląda jak poprawny prefixCode -> RTD_URI
      if (first < URI_PREFIXES.length) {
        const prefix = URI_PREFIXES[first] ?? ''
        const rest = new TextDecoder('utf-8', { fatal: false }).decode(
          bytes.slice(1)
        )
        return fixPossiblyBrokenUrl(prefix + rest)
      }

      // W przeciwnym razie: traktuj payload jako “goły” UTF‑8 (bez ucinania 1 bajtu)
      const raw = new TextDecoder('utf-8', { fatal: false }).decode(bytes)
      return fixPossiblyBrokenUrl(raw)
    } catch {
      return null
    }
  }

  function stopReading() {
    try {
      readAbortRef.current?.abort()
      readAbortRef.current = null

      const ndef = readerRef.current
      if (ndef) {
        if (onReadingRef.current)
          ndef.removeEventListener('reading', onReadingRef.current as any)
        if (onReadingErrorRef.current)
          ndef.removeEventListener(
            'readingerror',
            onReadingErrorRef.current as any
          )
      }
      readerRef.current = null
      onReadingRef.current = null
      onReadingErrorRef.current = null
    } finally {
      setIsReading(false)
    }
  }

  async function handleReadClick() {
    if (!session) return

    if (!isNFCSupported()) {
      setStatusKey('status.unsupported')
      return
    }

    if (isReading) {
      stopReading()
      setStatusKey('status.read_stopped')
      return
    }

    try {
      setTagSerial(null)
      setStatusKey('status.reading')

      const abortController = new AbortController()
      readAbortRef.current = abortController

      const ndef = new NDEFReader()
      readerRef.current = ndef

      setIsReading(true)

      const onReading = (event: any) => {
        const { message, serialNumber } = event as NDEFMessageEvent
        setTagSerial(serialNumber ?? null)

        let foundWjo2 = false
        let foundWjo3 = false
        let foundUrl: string | null = null

        for (const record of message.records) {
          // URL
          if (record.recordType === 'url') {
            const url = decodeUriRecord(record)
            if (url) foundUrl = url
            continue
          }

          // Markers (universal)
          const textLike = decodeAnyRecordToString(record)
          if (!textLike) continue

          const v = normalizeMarker(textLike)
          if (v === 'wjo2' || v === normalizeMarker(WJO_MARKERS.wjo2)) foundWjo2 = true
          if (v === 'wjo3' || v === normalizeMarker(WJO_MARKERS.wjo3)) foundWjo3 = true
        }

        setTargets({ wjo2: foundWjo2, wjo3: foundWjo3 })

        if (foundUrl) {
          setAddUrl(true)
          setUrlToWrite(foundUrl)
        } else {
          setAddUrl(false)
          setUrlToWrite(defaultsRef.current.urlToWrite)
        }

        setStatusKey('status.read_ok')
        stopReading()
      }

      const onReadingError = () => {
        setStatusKey('status.read_error')
        stopReading()
      }

      onReadingRef.current = onReading
      onReadingErrorRef.current = onReadingError

      await ndef.scan({ signal: abortController.signal })
      ndef.addEventListener('reading', onReading, { once: true })
      ndef.addEventListener('readingerror', onReadingError, { once: true })
    } catch {
      setStatusKey('status.read_error')
      stopReading()
    }
  }

  async function handleWriteClick() {
    if (!session) return

    if (!isNFCSupported()) {
      setStatusKey('status.unsupported')
      return
    }
    if (isReading) {
      setStatusKey('status.stop_reading_before_write')
      return
    }
    if (isWritingRef.current) return
    isWritingRef.current = true

    try {
      const selectedMarkers: Array<typeof WJO_MARKERS[keyof typeof WJO_MARKERS]> = []
      if (targetsRef.current.wjo2) selectedMarkers.push(WJO_MARKERS.wjo2)
      if (targetsRef.current.wjo3) selectedMarkers.push(WJO_MARKERS.wjo3)
      const shouldAddUrl = addUrlRef.current
      const urlValue = urlToWriteRef.current || originLoginUrl

      const urlRecordBinary: NDEFRecordInit = {
        recordType: 'url',
        data: encodeUriPayload(urlValue),
      }

      const urlRecordString: NDEFRecordInit = {
        recordType: 'url',
        data: urlValue,
      }

      const recordsToWriteBinary: NDEFRecordInit[] = [
        ...selectedMarkers.map((m) => ({ recordType: 'text', data: m })),
        ...(shouldAddUrl ? [urlRecordBinary] : []),
      ]

      const recordsToWriteString: NDEFRecordInit[] = [
        ...selectedMarkers.map((m) => ({ recordType: 'text', data: m })),
        ...(shouldAddUrl ? [urlRecordString] : []),
      ]

      const recordsToWrite =
        (shouldAddUrl ? recordsToWriteBinary : recordsToWriteString)

      if (recordsToWrite.length === 0) {
        setStatusKey('status.nothing_to_write')
        return
      }

      setStatusKey('status.writing')

      const writer = new NDEFReader()

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await (writer as any).write(
            { records: recordsToWrite },
            { overwrite: true }
          )
          setStatusKey('status.write_ok')
          return
        } catch (e) {
          // Fallback: jeśli binary URL nie przechodzi na tej implementacji,
          // spróbuj zapisać URL jako string (bardziej kompatybilne z Web NFC).
          if (shouldAddUrl) {
            try {
              await (writer as any).write(
                { records: recordsToWriteString },
                { overwrite: true }
              )
              setStatusKey('status.write_ok')
              return
            } catch {
              // continue to retry loop below
            }
          }

          if (attempt === 3) throw e
          await new Promise((r) => setTimeout(r, 200))
        }
      }
    } catch (error) {
      console.error('Write failed:', error)
      setStatusKey('status.write_error')
    } finally {
      isWritingRef.current = false
    }
  }

  useEffect(() => {
    return () => stopReading()
  }, [])

  if (!session) {
    return <Spinner />
  }

  const baseKey = 'elements.nfcwriter'
  const t = (k: string) => session.translates.for(`${baseKey}.${k}`)

  return (
    <LoggedInPageLayout
      pageName="nfcwriter"
      services={services}
      session={session}
      navbarLeft={
        <BackButton services={services} translates={session.translates} />
      }
    >
      <div className="nfcwriter-rows">
        <div className="nfcwriter-row nfcwriter-row--tag">
          <div className="nfcwriter-label">{t('tag.label')}</div>
          <div className="nfcwriter-value nfcwriter-value--mono">
            {tagSerial ?? t('tag.none')}
          </div>
        </div>

        {statusKey && (
          <div className="nfcwriter-row nfcwriter-row--status">
            {t(statusKey)}
          </div>
        )}

        <div className="nfcwriter-row nfcwriter-row--actions">
          <button className="nfcwriter-btn" onClick={handleReadClick}>
            {isReading ? t('buttons.stop_reading') : t('buttons.read')}
          </button>

          <button
            className="nfcwriter-btn"
            onClick={handleWriteClick}
            disabled={isReading}
          >
            {t('buttons.write')}
          </button>
        </div>

        <div className="nfcwriter-row nfcwriter-row--markers">
          <div className="nfcwriter-label">{t('markers.label')}</div>

          <label className="nfcwriter-check">
            <input
              type="checkbox"
              checked={targets.wjo2}
              onChange={(e) =>
                setTargets((prev) => ({ ...prev, wjo2: e.target.checked }))
              }
            />
            <span>{WJO_MARKERS.wjo2}</span>
          </label>

          <label className="nfcwriter-check">
            <input
              type="checkbox"
              checked={targets.wjo3}
              onChange={(e) =>
                setTargets((prev) => ({ ...prev, wjo3: e.target.checked }))
              }
            />
            <span>{WJO_MARKERS.wjo3}</span>
          </label>

          <label className="nfcwriter-check nfcwriter-check--url">
            <input
              type="checkbox"
              checked={addUrl}
              onChange={(e) => setAddUrl(e.target.checked)}
            />
            <span>{t('url.add')}</span>
          </label>
        </div>

        {addUrl && (
          <div className="nfcwriter-row nfcwriter-row--url">
            <div className="nfcwriter-label">{t('url.label')}</div>
            <input
              className="nfcwriter-input"
              value={urlToWrite}
              readOnly
              onChange={(e) => setUrlToWrite(e.target.value)}
            />
          </div>
        )}
      </div>
    </LoggedInPageLayout>
  )
}
